import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Alert from '../components/Alert';
import Confirm from '../components/Confirm';
import Toast from '../components/Toast';
import DataTable from '../components/DataTable';
import { ubigeoService } from '../services/api';
import { getErrorDetail } from '../utils/errorHelper';
import '../styles/datatable.css';

const Ubigeos = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [alert, setAlert] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);

  const emptyForm = { codigo: '', departamento: '', provincia: '', distrito: '' };
  const [form, setForm] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ubigeoService.getAll();
      setRecords(res.data);
    } catch {
      setAlert({ type: 'error', message: 'Error al cargar ubigeos.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    window._dtImportHandler = async (rows) => {
      let ok = 0, fail = 0;
      const total = rows.length;
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const codigo = row['Codigo'] || row['codigo'] || '';
        const departamento = row['Departamento'] || row['departamento'] || '';
        const provincia = row['Provincia'] || row['provincia'] || '';
        const distrito = row['Distrito'] || row['distrito'] || '';
        let errorMsg = null;
        if (!codigo || !departamento || !provincia || !distrito) {
          fail++;
          errorMsg = `Fila ${i + 1}: campos obligatorios vacios (Codigo, Departamento, Provincia, Distrito)`;
        } else {
          try {
            await ubigeoService.create({ codigo: String(codigo), departamento, provincia, distrito });
            ok++;
          } catch (err) {
            fail++;
            errorMsg = `Fila ${i + 1} ("${codigo}"): ${getErrorDetail(err, 'Error al crear ubigeo')}`;
          }
        }
        if (window._dtImportProgress) window._dtImportProgress(i + 1, total, ok, fail, errorMsg);
      }
      fetchData();
    };
    return () => { window._dtImportHandler = null; };
  }, [fetchData]);

  const openCreate = () => { setForm(emptyForm); setEditing(null); setShowModal(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ codigo: r.codigo || '', departamento: r.departamento || '', provincia: r.provincia || '', distrito: r.distrito || '' });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.codigo || !form.departamento || !form.provincia || !form.distrito) {
      setAlert({ type: 'warning', message: 'Todos los campos son obligatorios.' });
      return;
    }
    try {
      if (editing) await ubigeoService.update(editing.id_ubigeo, form);
      else await ubigeoService.create(form);
      setShowModal(false);
      fetchData();
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al guardar.') });
    }
  };

  const handleDelete = (r) => {
    setConfirm({
      title: 'Eliminar Ubigeo',
      message: `¿Eliminar "${r.departamento} / ${r.provincia} / ${r.distrito}"?`,
      onConfirm: async () => {
        setConfirm(null);
        try { await ubigeoService.delete(r.id_ubigeo); setToast('Ubigeo eliminado'); fetchData(); }
        catch { setAlert({ type: 'error', message: 'Error al eliminar.' }); }
      },
    });
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const columns = [
    { key: 'codigo', label: 'Codigo', width: 90, style: { fontWeight: 600 } },
    { key: 'departamento', label: 'Departamento', width: 160 },
    { key: 'provincia', label: 'Provincia', width: 160 },
    { key: 'distrito', label: 'Distrito' },
  ];

  const importCols = [
    { key: 'Codigo', label: 'Codigo', required: true, example: '150101', description: 'Codigo INEI de 6 digitos' },
    { key: 'Departamento', label: 'Departamento', required: true, example: 'LIMA', description: 'Nombre del departamento' },
    { key: 'Provincia', label: 'Provincia', required: true, example: 'LIMA', description: 'Nombre de la provincia' },
    { key: 'Distrito', label: 'Distrito', required: true, example: 'LIMA', description: 'Nombre del distrito' },
  ];

  return (
    <Layout title="Ubigeos" subtitle="Utilitarios">
      <div className="page-header">
        <h2>Ubigeos</h2>
        <button className="btn btn-primary" onClick={openCreate}>+ Nuevo</button>
      </div>

      <DataTable columns={columns} data={records} idField="id_ubigeo" onEdit={openEdit} onDelete={handleDelete}
        loading={loading} emptyMessage="No hay ubigeos registrados" exportFileName="ubigeos" importColumns={importCols} />

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <h2>{editing ? 'Editar Ubigeo' : 'Ubigeo'}</h2>
                {!editing && <span className="modal-header-badge"><svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" /></svg></span>}
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <label>Codigo INEI *</label>
                  <input name="codigo" value={form.codigo} onChange={handleChange} placeholder="Ej: 150101" maxLength={10} autoFocus />
                </div>
                <div className="form-row">
                  <label>Departamento *</label>
                  <input name="departamento" value={form.departamento} onChange={handleChange} placeholder="Ej: LIMA" />
                </div>
                <div className="form-row">
                  <label>Provincia *</label>
                  <input name="provincia" value={form.provincia} onChange={handleChange} placeholder="Ej: LIMA" />
                </div>
                <div className="form-row">
                  <label>Distrito *</label>
                  <input name="distrito" value={form.distrito} onChange={handleChange} placeholder="Ej: MIRAFLORES" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">{editing ? 'Guardar' : 'Crear'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {alert && <Alert type={alert.type} title={alert.title} message={alert.message} onClose={() => setAlert(null)} />}
      {confirm && <Confirm title={confirm.title} message={confirm.message} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </Layout>
  );
};

export default Ubigeos;
